import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'

export type PremiumSource = 'none' | 'comp' | 'subscription'

export interface AdminUserListItem {
  id: string
  email: string
  name: string
  role: string
  isAdmin: boolean
  premiumUntil: string | null
  createdAt: string
  premiumSource: PremiumSource
}

interface AdminUsersResponse {
  items: AdminUserListItem[]
  total: number
  limit: number
  offset: number
}

export interface AdminSubscription {
  id: string
  stripeCustomerId: string
  stripeSubscriptionId: string | null
  stripePriceId: string | null
  status: string
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
}

export interface AdminUserDetail {
  user: {
    id: string
    email: string
    name: string
    role: string
    isAdmin: boolean
    premiumUntil: string | null
    premiumGrantSessionId: string | null
    locale: string
    createdAt: string
    premiumSource: PremiumSource
  }
  subscriptions: AdminSubscription[]
  linkedin: {
    usedThisMonth: number
    monthlyLimit: number | null
    totalImports: number
    recentErrors: Array<{
      id: string
      profileUrl: string | null
      error: string | null
      importedAt: string
    }>
  }
}

export function useAdminUsers(search: string) {
  const params = new URLSearchParams()
  if (search.trim()) params.set('search', search.trim())
  const qs = params.toString()
  return useQuery({
    queryKey: ['admin', 'users', search.trim()],
    queryFn: () => api<AdminUsersResponse>(`/api/admin/users${qs ? `?${qs}` : ''}`),
  })
}

export function useAdminUser(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => api<AdminUserDetail>(`/api/admin/users/${id}`),
    enabled: !!id,
  })
}

function useAdminAction(action: string, successMsg: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: Record<string, unknown> }) =>
      api<{ ok: boolean }>(`/api/admin/users/${id}/${action}`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['admin', 'user', id] })
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      toast.success(successMsg)
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useGrantPremium() {
  return useAdminAction('grant', 'Premium granted')
}

export function useRevokePremium() {
  return useAdminAction('revoke', 'Premium revoked')
}

export function useReconcileUser() {
  return useAdminAction('reconcile', 'Reconciled from Stripe')
}
